/**
 * BookGrid — responsive shelf of book covers.
 */

import React from 'react';
import { motion } from 'framer-motion';
import BookCard from './BookCard';
import EmptyState from './EmptyState';
import { fadeUp, stagger } from '@/lib/motion';
import { BOOKSHELF } from '@/constants/testIds';

function BookCardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-[2/3] rounded-r-md rounded-l-[4px] bg-line/60" />
      <div className="mt-2.5 space-y-1.5">
        <div className="h-3 w-4/5 rounded bg-line/60" />
        <div className="h-2.5 w-1/2 rounded bg-line/50" />
      </div>
    </div>
  );
}

export default function BookGrid({
  books,
  isLoading,
  isEmpty,
  emptyStateType = 'all',
  emptyStateOverrides,
  onEmptyCTAClick,
  onBookOpen,
}) {
  if (isLoading) {
    return (
      <div className="u-container mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 md:gap-6">
        {[...Array(12)].map((_, i) => (
          <BookCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="u-container mt-8">
        <EmptyState type={emptyStateType} onCTAClick={onEmptyCTAClick} {...emptyStateOverrides} />
      </div>
    );
  }

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="show"
      className="u-container mt-8 grid grid-cols-2 gap-5 pb-16 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 md:gap-x-6 md:gap-y-9"
      data-testid={BOOKSHELF.bookGrid}
    >
      {books.map((book, i) => (
        <motion.div key={book.id} variants={fadeUp} custom={i % 12}>
          <BookCard book={book} onOpenDetails={onBookOpen} />
        </motion.div>
      ))}
    </motion.div>
  );
}
